import { forwardRef } from 'react';
import { Avatar, Box, Button, Card, CircularProgress, Stack, Tooltip, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import AppsRoundedIcon from '@mui/icons-material/AppsRounded';
import StatusBadge from '../status/StatusBadge';
import { healthCounts } from './health';

/**
 * One app of a site (app is its entry from /social/apps/): the site with the app's name, its connected accounts and
 * the login that adds an account through it. Clicking the card (selected) narrows the connections table to the app;
 * adding shows its login in progress; an inactive app cannot log in.
 */
export default function AppCard({ app, meta, selected, adding, disabled, onSelect, onLogin }) {
  const Icon = meta.icon;
  const inactive = app.is_active === false;
  const toggle = (event) => {
    if (event.type === 'keydown' && event.key !== 'Enter' && event.key !== ' ') return;
    event.preventDefault();
    onSelect();
  };

  return (
    <Card role="button" tabIndex={0} aria-pressed={selected} onClick={toggle} onKeyDown={toggle}
          sx={(theme) => ({
            p: 2.5, height: '100%', display: 'flex', flexDirection: 'column', gap: 2, cursor: 'pointer',
            borderColor: selected ? alpha(theme.palette.primary.main, 0.5) : 'divider',
            boxShadow: selected ? `0 0 0 3px ${alpha(theme.palette.primary.main, 0.12)}` : 'none',
            transition: 'border-color 0.15s, box-shadow 0.15s',
            '&:hover': { borderColor: selected ? alpha(theme.palette.primary.main, 0.6) : theme.palette.grey[400] },
            '&:focus-visible': { outline: `2px solid ${theme.palette.primary.main}`, outlineOffset: 2 },
          })}>
      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ minWidth: 0 }}>
        <Avatar variant="rounded" sx={{ bgcolor: meta.color, width: 44, height: 44, borderRadius: 2.5 }}>
          {Icon ? <Icon /> : meta.name[0]}
        </Avatar>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap', rowGap: 0.5, minWidth: 0 }}>
          <Typography variant="h6" component="h2" sx={{ fontWeight: 600, lineHeight: 1.3 }}>{meta.name}</Typography>
          {/* the app being connected through, by the name given in <site>.json */}
          {app.app_name && (
            <Tooltip title={`The ${meta.name} app connections are made through`}>
              <AppTag name={app.app_name} color={meta.color} />
            </Tooltip>
          )}
          {inactive && <StatusBadge tone="archived" label="Inactive app" />}
        </Stack>
      </Stack>

      <AccountTotal connections={app.connections} color={meta.color} />

      {/* the login always sits in the bottom right corner, however many counts there are */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 'auto' }}>
        <Tooltip title={inactive ? 'This app is no longer in the app list, so it cannot log in' : ''}>
          <span>
            <Button variant="contained" onClick={(event) => {
              event.stopPropagation();
              onLogin();
            }} onKeyDown={(event) => event.stopPropagation()} disabled={disabled || inactive}
                    startIcon={adding ? <CircularProgress size={18} color="inherit" /> : Icon && <Icon />}
                    sx={{ whiteSpace: 'nowrap', bgcolor: meta.color,
                      '&:hover': { bgcolor: meta.color, filter: 'brightness(0.92)' } }}>
              {adding ? 'Connecting…' : `Login with ${meta.name}`}
            </Button>
          </span>
        </Tooltip>
      </Box>
    </Card>
  );
}

// "APP  TrialX Test": an icon in the site colour, a small caps label, then the app's name
const AppTag = forwardRef(({ name, color, ...props }, ref) => (
  <Box ref={ref} component="span" {...props} sx={{
    display: 'inline-flex', alignItems: 'center', gap: 0.75, height: 28, pl: 0.5, pr: 1.25, borderRadius: 999,
    border: 1, borderColor: 'divider', bgcolor: 'background.paper', boxShadow: '0 1px 2px rgba(37, 45, 82, 0.05)',
    maxWidth: 280,
  }}>
    <Box component="span" sx={{ display: 'grid', placeItems: 'center', width: 20, height: 20, borderRadius: '50%',
      bgcolor: alpha(color, 0.12), color, flexShrink: 0 }}>
      <AppsRoundedIcon sx={{ fontSize: 13 }} />
    </Box>
    <Box component="span" sx={{ fontSize: 10, fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase',
      color: 'text.secondary', flexShrink: 0 }}>
      App
    </Box>
    <Box component="span" sx={{ fontSize: 13, fontWeight: 600, color: 'text.primary', whiteSpace: 'nowrap',
      overflow: 'hidden', textOverflow: 'ellipsis' }}>
      {name}
    </Box>
  </Box>
));

// Connected accounts, then how many are not working or expiring soon when there are any
function AccountTotal({ connections, color }) {
  const { error, warning } = healthCounts(connections);
  return (
    <Stack direction="row" spacing={0.75} useFlexGap sx={{ flexWrap: 'wrap' }}>
      <Stat count={connections.length} label={`Connected account${connections.length === 1 ? '' : 's'}`}
            color={color} />
      {error > 0 && <Stat count={error} label="Not working" color={(theme) => theme.palette.error.main} />}
      {warning > 0 && <Stat count={warning} label="Expiring soon" color={(theme) => theme.palette.warning.main} />}
    </Stack>
  );
}

// A count with its label as a small pill tinted with color (a colour or a theme => colour function)
function Stat({ count, label, color }) {
  return (
    <Box component="span" sx={(theme) => {
      const value = typeof color === 'function' ? color(theme) : color;
      return {
        display: 'inline-flex', alignItems: 'center', gap: 0.75, height: 26, px: 1.25, borderRadius: 999,
        bgcolor: alpha(value, 0.08), fontSize: 13, whiteSpace: 'nowrap', '& .count': { color: value },
      };
    }}>
      <Box component="span" className="count" sx={{ fontWeight: 700 }}>{count}</Box>
      <Box component="span" sx={{ color: 'text.secondary', fontWeight: 500 }}>{label}</Box>
    </Box>
  );
}
